import React, { useState, useEffect } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Button, Grid, Paper, CircularProgress, Alert, Tabs, Tab } from '@mui/material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import config from '../config';
import CosmeticsDashboard from '../components/analysis/CosmeticsDashboard';
import Sapin2Dashboard from '../components/analysis/Sapin2Dashboard';
import EmailActivityDashboard from '../components/analysis/EmailActivityDashboard';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const Analysis = () => {
    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState('');
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [mainTabIndex, setMainTabIndex] = useState(0);

    useEffect(() => {
        fetchTables();
    }, []);

    useEffect(() => {
        if (selectedTable) {
            fetchStats();
        }
    }, [selectedTable]);

    const fetchTables = async () => {
        try {
            const response = await axios.get(`${config.API_BASE_URL}/fournisseurs`);
            const tablesData = response.data;
            setTables(tablesData);

            // Fetch default table setting
            const defaultResponse = await axios.get(`${config.API_BASE_URL}/settings/default-table`);
            const defaultTableName = defaultResponse.data.value;

            if (defaultTableName && tablesData.some(t => (typeof t === 'string' ? t : t.name) === defaultTableName)) {
                setSelectedTable(defaultTableName);
            } else if (tablesData.length > 0) {
                const firstTable = tablesData[0];
                setSelectedTable(typeof firstTable === 'string' ? firstTable : (firstTable.name || ''));
            }
        } catch (err) {
            console.error(err);
            setError('Failed to load tables');
        }
    };

    const fetchStats = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${config.API_BASE_URL}/analysis/stats/${selectedTable}`);
            setStats(response.data);
        } catch (err) {
            console.error(err);
            setStats(null);
        } finally {
            setLoading(false);
        }
    };

    const handleLaunchAnalysis = async () => {
        setMessage(null);
        setError(null);
        await fetchStats();
        setMessage('Analysis successfully launched!');
    };

    const handleMainTabChange = (event, newValue) => {
        setMainTabIndex(newValue);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">Analysis & Reporting</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleLaunchAnalysis}
                        disabled={!selectedTable || loading}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Launch Analysis'}
                    </Button>
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel id="table-select-label">Select Table</InputLabel>
                        <Select
                            labelId="table-select-label"
                            value={selectedTable}
                            label="Select Table"
                            onChange={(e) => setSelectedTable(e.target.value)}
                        >
                            {tables.map((table) => (
                                <MenuItem key={typeof table === 'string' ? table : table.name} value={typeof table === 'string' ? table : table.name}>
                                    {typeof table === 'string' ? table : table.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={mainTabIndex} onChange={handleMainTabChange}>
                    <Tab label="Vue d'ensemble" />
                    <Tab label="Devoir de Vigilance (Cosmétique)" />
                    <Tab label="Loi Sapin II (Anticorruption)" />
                    <Tab label="Email Activity" />
                </Tabs>
            </Box>

            {/* TAB 0: OVERVIEW (Existing Logic) */}
            {mainTabIndex === 0 && (
                <Box>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : stats && stats.distribution && stats.distribution.length > 0 ? (
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Typography variant="h6">Total Suppliers</Typography>
                                    <Typography variant="h3">{stats.total}</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Typography variant="h6">Average Risk Score</Typography>
                                    <Typography variant="h3">{stats.avgScore}</Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={12}>
                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>Risk Distribution</Typography>
                                    <Box sx={{ width: '100%', height: 300 }}>
                                        <ResponsiveContainer>
                                            <BarChart
                                                data={stats.distribution}
                                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="risk_level" interval={0} tick={{fontSize: 11}} height={60} angle={-15} textAnchor="end" />
                                                <YAxis width={60} />
                                                <Tooltip />
                                                <Legend />
                                                <Bar dataKey="count" name="Nombre de fournisseurs">
                                                    {stats.distribution.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color || '#8884d8'} />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </Paper>
                            </Grid>

                            {stats.scoreDistribution && stats.scoreDistribution.length > 0 && (
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>
                                            Évaluation de Niveau 1
                                        </Typography>
                                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                            <thead>
                                                <Box component="tr" sx={{ borderBottom: '2px solid #333' }}>
                                                    <Box component="th" sx={{ textAlign: 'right', p: '4px 8px', width: '10%' }}>Score</Box>
                                                    <Box component="th" sx={{ textAlign: 'left', p: '4px 8px' }}>Niveau de risque</Box>
                                                    <Box component="th" sx={{ textAlign: 'right', p: '4px 8px', width: '15%' }}>Nb</Box>
                                                    <Box component="th" sx={{ textAlign: 'right', p: '4px 8px', width: '15%' }}>%</Box>
                                                </Box>
                                            </thead>
                                            <tbody>
                                                {stats.scoreDistribution.map((row, i) => (
                                                    <Box component="tr" key={i} sx={{ borderBottom: '1px solid #eee', '&:hover': { bgcolor: '#f5f5f5' } }}>
                                                        <Box component="td" sx={{ textAlign: 'right', p: '4px 8px', fontWeight: 'bold', color: row.score === 'N/A' ? '#9e9e9e' : row.color }}>{row.score}</Box>
                                                        <Box component="td" sx={{ p: '4px 8px', fontWeight: 'bold', color: row.score === 'N/A' ? '#9e9e9e' : row.color }}>{row.label}</Box>
                                                        <Box component="td" sx={{ textAlign: 'right', p: '4px 8px', fontWeight: 'bold', color: row.score === 'N/A' ? '#9e9e9e' : row.color }}>{row.count.toLocaleString()}</Box>
                                                        <Box component="td" sx={{ textAlign: 'right', p: '4px 8px', fontWeight: 'bold', color: row.score === 'N/A' ? '#9e9e9e' : row.color }}>{row.percentage}%</Box>
                                                    </Box>
                                                ))}
                                                <Box component="tr" sx={{ borderTop: '2px solid #333', fontWeight: 'bold' }}>
                                                    <Box component="td" sx={{ p: '4px 8px' }} />
                                                    <Box component="td" sx={{ p: '4px 8px', fontWeight: 'bold' }}>TOTAL</Box>
                                                    <Box component="td" sx={{ textAlign: 'right', p: '4px 8px', fontWeight: 'bold' }}>{stats.total.toLocaleString()}</Box>
                                                    <Box component="td" sx={{ textAlign: 'right', p: '4px 8px', fontWeight: 'bold' }}>100%</Box>
                                                </Box>
                                            </tbody>
                                        </Box>
                                    </Paper>
                                </Grid>
                            )}

                            {stats.geoDistribution && stats.geoDistribution.length > 0 && (
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>Répartition Géographique (Top 15)</Typography>
                                        <Box sx={{ width: '100%', height: 600 }}>
                                            <ResponsiveContainer>
                                                <BarChart
                                                    data={stats.geoDistribution}
                                                    layout="vertical"
                                                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis type="number" hide />
                                                    <YAxis dataKey="name" type="category" width={120} tick={{fontSize: 11}} interval={0} />
                                                    <Tooltip />
                                                    <Legend />
                                                    <Bar dataKey="value" fill="#8884d8" name="Nombre de fournisseurs" />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </Paper>
                                </Grid>
                            )}

                            {stats.spendDistribution && stats.spendDistribution.length > 0 && (
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>Répartition par Montant (Buckets)</Typography>
                                        <Box sx={{ width: '100%', height: 400 }}>
                                            <ResponsiveContainer>
                                                <BarChart
                                                    data={stats.spendDistribution}
                                                    margin={{ top: 10, right: 30, left: 40, bottom: 20 }}
                                                    layout="vertical"
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis type="number" hide />
                                                    <YAxis dataKey="name" type="category" width={140} tick={{fontSize: 11}} interval={0} />
                                                    <Tooltip />
                                                    <Legend />
                                                    <Bar dataKey="value" fill="#8884d8" name="Nombre de fournisseurs" />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </Paper>
                                </Grid>
                            )}

                            {stats.natureDistribution && stats.natureDistribution.length > 0 && (
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>Répartition par Nature de tiers (Top 15)</Typography>
                                        <Box sx={{ width: '100%', height: 800 }}>
                                            <ResponsiveContainer>
                                                <BarChart
                                                    data={stats.natureDistribution.slice(0, 15)}
                                                    layout="vertical"
                                                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis type="number" hide />
                                                    <YAxis dataKey="name" type="category" width={200} tick={{fontSize: 11}} interval={0} />
                                                    <Tooltip />
                                                    <Legend />
                                                    <Bar dataKey="value" fill="#00C49F" name="Nombre de fournisseurs" />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                    ) : (
                        <Alert severity="info">
                            No analysis data available. Please select a table and click "Launch Analysis".
                        </Alert>
                    )}
                </Box>
            )}

            {/* TAB 1: COSMETICS DASHBOARD */}
            {mainTabIndex === 1 && <CosmeticsDashboard />}

            {/* TAB 2: SAPIN II DASHBOARD */}
            {mainTabIndex === 2 && <Sapin2Dashboard />}

            {/* TAB 3: EMAIL ACTIVITY DASHBOARD */}
            {mainTabIndex === 3 && <EmailActivityDashboard />}
        </Box>
    );
};

export default Analysis;
